import { useState, useEffect, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom/";

import { getConfig } from "../../../config/config.js";
import { useAuth } from "../../../AuthContext";
import { checkPermisos } from "../../utils/checkPermisos.js";
import { paginate } from "../../utils/paginate.js";
import { toastConfirm, toastError } from "../../utils/toasts";

import _ from "lodash";

export function useCrud({
  defaultPath,
  defaultSortColumn = { path: "", order: "" },
  hookWillMount,
  onDelete,
  checkBeforeDelete,
  filterData,
  onLoading,
  onDoneLoading,
}) {
  const config = getConfig();
  const pageSize = config.recordsPerPage;
  const history = useHistory();

  const { activeUser, permisos: allowedTo } = useAuth();
  const [allRecords, setAllRecords] = useState([]);
  const [records, setRecords] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState(defaultSortColumn);
  const [searchQuery, setSearchQuery] = useState("");
  const [permisos, setPermisos] = useState({});
  const [mounted, setMounted] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  // const [loading, setLoading] = useState(false);

  /*------------*/

  const goBack = () => {
    history.goBack();
  };

  const useComponentWillMount = (func) => {
    const willMount = useRef(true);
    if (willMount.current) {
      func();
      willMount.current = false;
    }
  };

  useComponentWillMount(() => {
    setPermisos(checkPermisos(defaultPath, allowedTo, activeUser, history));
    if (hookWillMount) hookWillMount();
  });

  const updateAllRecords = useCallback((newRecords) => {
    if (!newRecords) return;
    setAllRecords(newRecords);
  }, []);

  useEffect(() => {
    const tCount = getPagedData();
    setTotalCount(tCount);
    if (currentPage > Math.ceil(tCount / pageSize) && tCount > 0) setCurrentPage(currentPage);
  }, [allRecords, currentPage, sortColumn, searchQuery]);

  const handleDelete = async (id) => {
    let willDelete = true;
    if (checkBeforeDelete) {
      const canDelete = await checkBeforeDelete(id);
      if (!canDelete) {
        toastError("No se puede eliminar este registro ya que está relacionado a otros documentos.", 5000);
        willDelete = false;
      }
    }
    if (willDelete) toastConfirm("¿ Eliminar registro ?", () => doDelete(id));
  };

  const doDelete = async (id) => {
    const originalRecords = records;
    const newRecords = records.filter((m) => m._id !== id);
    setRecords(newRecords);
    try {
      await onDelete(id);
    } catch (ex) {
      if (ex.response && ex.response.status === 404) toastError("Ese registro ya fue eliminado");
      setRecords(originalRecords);
    }
  };

  const handleSort = (sortColumn) => {
    setCurrentPage(1);
    setSortColumn(sortColumn);
  };

  const handleEdit = (id) => {
    history.push({
      pathname: defaultPath + "/" + id,
      state: { action: "E" },
    });
  };

  const handleView = (id) => {
    history.push({
      pathname: defaultPath + "/" + id,
      state: { action: "C" },
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getPagedData = () => {
    let filtered = [];
    if (filterData && searchQuery.trim().length > 0) {
      filtered = filterData(allRecords, searchQuery);
      // filtered = allRecords.filter((m) => cleanString(m.descripcion).includes(cleanString(searchQuery)));
    } else {
      filtered = allRecords;
    }
    // if (typeof sortColumn.path === "function") console.log(sortColumn.path(filtered[0]));
    const ordered = _.orderBy(
      filtered,
      [
        (item) =>
          typeof sortColumn.path !== "function" &&
          item[sortColumn.path].toLocaleLowerCase &&
          typeof item[sortColumn.path].toLocaleLowerCase === "function"
            ? item[sortColumn.path].toLocaleLowerCase()
            : typeof sortColumn.path === "function"
              ? sortColumn.path(item)
              : item[sortColumn.path],
      ],
      [sortColumn.order],
    );
    const registros = paginate(ordered, currentPage, pageSize);
    setRecords(registros);
    return filtered.length;
  };

  return {
    // state
    records,
    totalCount,
    pageSize,
    allRecords,
    permisos,
    currentPage,
    sortColumn,
    // loading,
    mounted,
    // setters (exposed so a form can fully override behavior if needed)
    updateAllRecords,
    setRecords,
    setMounted,
    setSearchQuery,
    setCurrentPage,
    // actions
    handleSort,
    handleView,
    handleEdit,
    handleDelete,
    handlePageChange,
  };
}
